import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/shared/contexts/ToastContext'
import { YakurekiDetail } from '@/shared/types/yakurekiCore'
import { Yakureki } from '@/shared/types/yakurekiCore'
import { SoapSectionKeys, SoapContent } from '@/shared/types/yakurekiSoap'
import {
  useUpdateSoapSelectionMutation,
  usePatchCopyDoneMutation,
} from '@/features/yakureki/hooks/queries/useSoapMutations'
import {
  formatSoapText as formatSoapTextNew,
  shouldUseSpecialFormat,
} from '@/features/formatter/utils/soapFormatter'
import {
  useSelectionFromYakureki,
  getSelectionFromCache,
} from '@/features/yakureki/hooks/queries/useSelectionQuery'

/**
 * SOAP操作関連のロジックを集約したカスタムフック
 *
 * @description
 * ADR-055 対応: TanStack Query + Optimistic UI パターンへの移行
 *
 * 責務:
 * - 選択状態の取得（TanStack Query キャッシュから）
 * - SOAP テキストのフォーマット（マスターデータから content 取得）
 * - クリップボードコピー（サーバー同期込み）
 *
 * 変更点（ADR-055）:
 * - formatSoapText はマスターデータ（yakurekiDetail.soaps）から content を取得
 *   → 問題2「医薬品置換後、古い名前がコピーされる」を解決
 * - copyToClipboard でサーバー同期を実行
 *   → 問題1「コピー後にリロードすると選択状態が初期状態に戻る」を解決
 *
 * 使用箇所: PostRecordingConfirmationScreen
 *
 * @see ADR-055: SelectionContext の Content 同期問題の根本的解決
 */
export const useSoapOperations = (
  yakurekiDetail: YakurekiDetail | null,
  selectedYakureki?: Yakureki
) => {
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  // ADR-055: TanStack Query から選択状態を取得（SelectionContext 依存を削除）
  const { selection, headerSelection } = useSelectionFromYakureki(
    selectedYakureki?.id
  )

  // TanStack Query Mutations
  const updateSelectionMutation = useUpdateSoapSelectionMutation()
  const patchCopyDoneMutation = usePatchCopyDoneMutation()

  /**
   * テキスト形式のSOAPを生成
   *
   * ADR-055 対応: マスターデータ（yakurekiDetail.soaps）から content を取得
   * → 問題2「医薬品置換後、古い名前がコピーされる」を解決
   */
  const formatSoapText = useCallback(() => {
    // 新シグネチャで呼び出し: selection と headerSelection はインデックスのみ
    // content はマスターデータ（yakurekiDetail.soaps）から取得
    return formatSoapTextNew(
      selection,
      headerSelection,
      yakurekiDetail?.soaps,
      { useSpecialFormat: shouldUseSpecialFormat() }
    )
  }, [selection, headerSelection, yakurekiDetail?.soaps])

  /**
   * SOAP選択状態をサーバーに同期
   *
   * 現在の選択状態をキャッシュから取得し、サーバーに送信
   */
  const syncSelectionToServer = useCallback(async () => {
    if (!selectedYakureki || !yakurekiDetail?.soaps) return

    // キャッシュから最新の選択状態を取得
    const { selection: currentSelection } = getSelectionFromCache(
      queryClient,
      selectedYakureki.id
    )

    const { id: _soapId, ...soapContent } = yakurekiDetail.soaps

    // 選択状態を SoapContent 形式に変換
    const updatedSoap: SoapContent = {
      subjectives: [],
      objectives: [],
      assessments: [],
      educational_plans: [],
      care_plans: [],
      observational_plans: [],
    }

    const sectionKeys: SoapSectionKeys[] = [
      'subjectives',
      'objectives',
      'assessments',
      'educational_plans',
      'care_plans',
      'observational_plans',
    ]

    for (const key of sectionKeys) {
      const items = soapContent[key] || []
      updatedSoap[key] = items.map((item, index) => ({
        ...item,
        is_selected: currentSelection[key].has(index),
      }))
    }

    try {
      await updateSelectionMutation.mutateAsync({
        yakurekiId: selectedYakureki.id,
        soapData: updatedSoap,
      })
    } catch {
      showToast(
        '選択状態の保存に失敗しました。ネットワーク接続を確認してください。',
        'warning',
        5000
      )
    }
  }, [
    selectedYakureki,
    yakurekiDetail,
    queryClient,
    showToast,
    updateSelectionMutation,
  ])

  /**
   * クリップボードにコピー
   *
   * ADR-055 対応:
   * - formatSoapText() でマスターデータから content を取得（問題2解決）
   * - syncSelectionToServer() でサーバー同期を実行（問題1解決）
   */
  const copyToClipboard = useCallback(async () => {
    const copyStr = formatSoapText()
    if (copyStr) {
      await navigator.clipboard.writeText(copyStr)
      showToast('コピーしました', 'info', 2000)

      // 問題1解決: 選択状態をサーバーに同期
      await syncSelectionToServer()

      // コピー済みフラグを更新
      if (selectedYakureki) {
        await patchCopyDoneMutation.mutateAsync(selectedYakureki.id)
      }
      return true
    }
    return false
  }, [
    formatSoapText,
    showToast,
    syncSelectionToServer,
    selectedYakureki,
    patchCopyDoneMutation,
  ])

  return {
    // SOAP操作
    formatSoapText,
    syncSelectionToServer,
    copyToClipboard,
  }
}
